import { jsPDF } from 'jspdf';
import { scoreBand } from './healthScore';
import { vehicleTitle } from './format';

const brand = {
  dark: [18, 18, 18],
  gold: [216, 171, 18],
  muted: [98, 98, 98],
  soft: [245, 247, 250],
  line: [224, 227, 231],
};

function scoreBandLabel(score) {
  const band = scoreBand(score);
  if (band === 'good') return 'Good';
  if (band === 'fair') return 'Fair';
  return 'Poor';
}

export function healthFileName(reportId) {
  const short = `${reportId || 'report'}`.slice(0, 8);
  return `health-report-${short}.pdf`;
}

async function tryLoadImageDataUrl(url) {
  if (!url) return null;
  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    const blob = await response.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(typeof reader.result === 'string' ? reader.result : null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

function imageFormatFromDataUrl(dataUrl) {
  if (!dataUrl || typeof dataUrl !== 'string') return null;
  const match = dataUrl.match(/^data:image\/([a-zA-Z0-9+.-]+);base64,/);
  if (!match) return null;
  const subtype = match[1].toLowerCase();
  if (subtype.includes('png')) return 'PNG';
  if (subtype.includes('jpg') || subtype.includes('jpeg')) return 'JPEG';
  if (subtype.includes('webp')) return 'WEBP';
  return null;
}

function branchLineFromBooking(booking) {
  const branch = booking?.branches;
  if (!branch) return '—';
  return `${branch.name || '—'}${
    branch.address ? ` · ${branch.address}` : ''
  }${branch.phone ? ` · ${branch.phone}` : ''}`;
}

function drawInfoCard(doc, { x, y, width, title, lines }) {
  doc.setDrawColor(...brand.line);
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(x, y, width, 94, 10, 10, 'FD');
  doc.setTextColor(...brand.dark);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text(title, x + 12, y + 20);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  let lineY = y + 40;
  for (const line of lines) {
    if (!line) continue;
    doc.text(line, x + 12, lineY, { maxWidth: width - 24 });
    lineY += 17;
  }
}

function drawTextSection(doc, { margin, y, contentWidth, title, body }) {
  const wrapped = doc.splitTextToSize(body, contentWidth - 24);
  const height = 28 + wrapped.length * 12;
  doc.setDrawColor(...brand.line);
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(margin, y, contentWidth, height, 10, 10, 'FD');
  doc.setTextColor(...brand.dark);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text(title, margin + 12, y + 18);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...brand.muted);
  doc.text(wrapped, margin + 12, y + 34);
  return height + 10;
}

export async function buildHealthPdf(report, context = {}) {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 36;
  const contentWidth = pageWidth - margin * 2;
  const business = context.business || {};
  const vehicle = context.vehicle || {};
  const booking = context.booking || {};
  const customer = context.customer || booking.profiles || {};

  const whenRaw = booking.scheduled_at || report.created_at;
  const when = whenRaw ? new Date(whenRaw).toLocaleString() : '—';
  const issued = report.finalized_at
    ? new Date(report.finalized_at).toLocaleDateString()
    : new Date().toLocaleDateString();
  const status = `OVERALL ${scoreBandLabel(report.overall_score).toUpperCase()} · ${report.overall_score}`;
  const logo = await tryLoadImageDataUrl(business.logo_url);
  const brandTitle = business.brand_name?.trim() || 'BW Station';
  const initials = brandTitle.slice(0, 3).toUpperCase();

  doc.setFillColor(...brand.dark);
  doc.rect(0, 0, pageWidth, 118, 'F');

  if (logo) {
    const format = imageFormatFromDataUrl(logo);
    if (format) doc.addImage(logo, format, margin, 20, 74, 48, undefined, 'FAST');
  } else {
    doc.setFillColor(...brand.gold);
    doc.roundedRect(margin, 20, 74, 48, 8, 8, 'F');
    doc.setTextColor(...brand.dark);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text(initials, margin + 37, 50, { align: 'center' });
  }

  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.text(brandTitle, margin + 90, 38);
  if (business.tagline) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(214, 214, 214);
    doc.text(business.tagline, margin + 90, 54);
  }
  const meta = [business.address, business.phone_primary, business.phone_secondary]
    .filter(Boolean)
    .join(' · ');
  if (meta) {
    doc.setFontSize(9.5);
    doc.text(meta, margin + 90, 68, { maxWidth: contentWidth - 250 });
  }
  const contact = [business.support_email, business.website].filter(Boolean).join(' · ');
  if (contact) doc.text(contact, margin + 90, 82, { maxWidth: contentWidth - 250 });

  doc.setFillColor(...brand.gold);
  doc.roundedRect(pageWidth - margin - 148, 24, 148, 40, 14, 14, 'F');
  doc.setTextColor(...brand.dark);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text(status, pageWidth - margin - 74, 48, { align: 'center' });

  let y = 140;
  doc.setFillColor(...brand.soft);
  doc.roundedRect(margin, y, contentWidth, 40, 10, 10, 'F');
  doc.setTextColor(...brand.dark);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text('VEHICLE HEALTH REPORT', margin + 14, y + 26);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...brand.muted);
  doc.text(`Issued ${issued}`, margin + contentWidth - 14, y + 26, { align: 'right' });
  y += 54;

  const cardWidth = (contentWidth - 10) / 2;
  drawInfoCard(doc, {
    x: margin,
    y,
    width: cardWidth,
    title: 'Vehicle details',
    lines: [
      `Vehicle: ${vehicleTitle(vehicle)}`,
      vehicle.plate_number ? `Plate: ${vehicle.plate_number.toUpperCase()}` : null,
      vehicle.vin ? `VIN: ${vehicle.vin.toUpperCase()}` : null,
      report.mileage != null ? `Mileage: ${Number(report.mileage).toLocaleString()} km` : null,
    ],
  });
  drawInfoCard(doc, {
    x: margin + cardWidth + 10,
    y,
    width: cardWidth,
    title: 'Visit details',
    lines: [
      `Service: ${booking.service_type || report.service_type || '—'}`,
      `Scheduled: ${when}`,
      `Branch: ${branchLineFromBooking(booking)}`,
      customer.full_name || customer.first_name
        ? `Customer: ${customer.full_name || customer.first_name}`
        : null,
    ],
  });
  y += 112;

  if (customer.phone || customer.email) {
    drawInfoCard(doc, {
      x: margin,
      y,
      width: contentWidth,
      title: 'Customer contact',
      lines: [
        customer.phone ? `Phone: ${customer.phone}` : null,
        customer.email ? `Email: ${customer.email}` : null,
      ],
    });
    y += 112;
  }

  doc.setFillColor(...brand.gold);
  doc.roundedRect(margin, y, contentWidth, 38, 8, 8, 'F');
  doc.setTextColor(...brand.dark);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.text('OVERALL HEALTH SCORE', margin + 12, y + 25);
  doc.text(`${report.overall_score} / 100`, margin + contentWidth - 12, y + 25, { align: 'right' });
  y += 52;

  const subsystems = [
    ['Engine', report.engine_score],
    ['Braking', report.braking_score],
    ['Tires', report.tires_score],
    ['Battery', report.battery_score],
    ['Cooling', report.cooling_score],
    ['Suspension', report.suspension_score],
    ['Electrical', report.electrical_score],
  ];

  doc.setFillColor(...brand.dark);
  doc.roundedRect(margin, y, contentWidth, 28, 6, 6, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('System', margin + 10, y + 18);
  doc.text('Score', margin + contentWidth - 10, y + 18, { align: 'right' });
  y += 34;

  subsystems.forEach(([label, score], idx) => {
    doc.setFillColor(idx % 2 === 0 ? 255 : 250, idx % 2 === 0 ? 255 : 250, idx % 2 === 0 ? 255 : 250);
    doc.roundedRect(margin, y, contentWidth, 30, 4, 4, 'F');
    doc.setTextColor(...brand.dark);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10.5);
    doc.text(label, margin + 10, y + 19);
    doc.text(`${score}`, margin + contentWidth - 10, y + 19, { align: 'right' });
    y += 36;
    if (y > pageHeight - 120) {
      doc.addPage();
      y = margin;
    }
  });

  const services = Array.isArray(report.overdue_services)
    ? report.overdue_services
        .filter((item) => item?.service)
        .map((item) =>
          item.notes ? `• ${item.service} — ${item.notes}` : `• ${item.service}`,
        )
        .join('\n')
    : '';
  if (services) {
    y += 6;
    y += drawTextSection(doc, {
      margin,
      y,
      contentWidth,
      title: 'Overdue / recommended services',
      body: services,
    });
  }
  if (report.diagnostic_findings?.trim()) {
    y += drawTextSection(doc, {
      margin,
      y,
      contentWidth,
      title: 'Diagnostics & warning lights',
      body: report.diagnostic_findings.trim(),
    });
  }
  if (report.accident_repair_notes?.trim()) {
    y += drawTextSection(doc, {
      margin,
      y,
      contentWidth,
      title: 'Accident / repair notes',
      body: report.accident_repair_notes.trim(),
    });
  }
  if (report.tire_condition || report.battery_condition) {
    y += drawTextSection(doc, {
      margin,
      y,
      contentWidth,
      title: 'Condition checks',
      body: [
        report.tire_condition ? `Tires: ${report.tire_condition}` : null,
        report.battery_condition ? `Battery: ${report.battery_condition}` : null,
      ]
        .filter(Boolean)
        .join('\n'),
    });
  }
  if (report.maintenance_history_notes?.trim()) {
    y += drawTextSection(doc, {
      margin,
      y,
      contentWidth,
      title: 'Maintenance notes',
      body: report.maintenance_history_notes.trim(),
    });
  }

  return doc;
}

export async function downloadHealthPdf(report, context) {
  const doc = await buildHealthPdf(report, context);
  doc.save(healthFileName(report.id));
}

export async function shareHealthPdf(report, context) {
  const doc = await buildHealthPdf(report, context);
  const blob = doc.output('blob');
  const file = new File([blob], healthFileName(report.id), { type: 'application/pdf' });
  if (navigator.share && navigator.canShare?.({ files: [file] })) {
    await navigator.share({
      title: `Health report ${report.overall_score}/100`,
      text: `Vehicle health report for ${vehicleTitle(context.vehicle || {})}`,
      files: [file],
    });
    return 'shared';
  }
  doc.save(healthFileName(report.id));
  return 'downloaded';
}
